import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { clearAuthToken, getAuthToken, setAuthToken } from '@/api/core/client';
import { onAuthExpired } from '@/lib/authSession';
import { AuthContext, type AuthContextValue, type AuthUser } from './AuthContext';

/**
 * Session state, backed by the shared new-auth-server on netcup (reached at
 * `/auth/*`, which Caddy strip-prefixes to port 3011). The token is the only
 * thing we persist; the user record and its permission list always come from
 * `/auth/me` so a permission granted or revoked server-side takes effect on
 * the next boot rather than living forever in localStorage.
 */
const AUTH_BASE = '/auth';

/** `orders:agent` is a server-side wildcard; the UI must agree with it. */
const WILDCARD_CODES = new Set(['*', 'orders:agent']);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getAuthToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  // Boot with a token means a /me is about to run: render nothing decisive
  // until it answers, or a permission-gated route flashes "No access" first.
  const [isLoading, setIsLoading] = useState<boolean>(() => Boolean(getAuthToken()));
  const [error, setError] = useState<string | null>(null);

  const logout = useCallback(() => {
    clearAuthToken();
    setToken(null);
    setUser(null);
    setIsLoading(false);
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    const current = getAuthToken();
    if (!current) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${AUTH_BASE}/me`, {
        headers: { Authorization: `Bearer ${current}` },
      });
      if (!response.ok) {
        // Only a genuine rejection ends the session. A 500 or a rate limit is
        // transient — keeping the token means a server blip doesn't sign
        // everyone out.
        if (response.status === 401 || response.status === 403) {
          logout();
          return;
        }
        const details = await response.json().catch(() => ({}));
        setError(details.error || `Auth check failed (${response.status})`);
        return;
      }
      setUser((await response.json()) as AuthUser);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Auth check failed');
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      void fetchCurrentUser();
    } else {
      setUser(null);
      setIsLoading(false);
    }
  }, [token, fetchCurrentUser]);

  // A 401 from any API call means the JWT expired mid-session.
  useEffect(() => onAuthExpired(logout), [logout]);

  // Re-validate after a long absence so a token that expired while the tab sat
  // in the background is discovered on return rather than on the next click.
  const lastCheckRef = useRef(Date.now());
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || !getAuthToken()) return;
      if (Date.now() - lastCheckRef.current < 5 * 60 * 1000) return;
      lastCheckRef.current = Date.now();
      void fetchCurrentUser();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [fetchCurrentUser]);

  const login = useCallback(async (username: string, password: string) => {
    setError(null);
    const response = await fetch(`${AUTH_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!response.ok) {
      const details = await response.json().catch(() => ({}));
      throw new Error(details.error || 'Login failed');
    }
    const data = (await response.json()) as { token: string; user: AuthUser };
    setAuthToken(data.token);
    setUser(data.user);
    setToken(data.token);
    setIsLoading(false);
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const permissions = new Set(user?.permissions ?? []);
    return {
      user,
      token,
      isLoading,
      error,
      login,
      logout,
      can: (code) => {
        if (!user) return false;
        if (user.is_admin) return true;
        if (permissions.has(code)) return true;
        for (const held of permissions) {
          if (WILDCARD_CODES.has(held)) return true;
        }
        return false;
      },
    };
  }, [user, token, isLoading, error, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
